import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getClients, submitIntakeForm, getIntakeForms, sendIntakeInstructions, uploadFile, getPendingIntakeRequests, resendIntakeShare, cancelIntakeShare, downloadIntakeFormDocx, API_BASE } from "../lib/api";
import { getAllForms, getFormById } from "../lib/intakeForms";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Globe, Target, Search, Phone, Loader2, FileText, CheckCircle2,
  ArrowLeft, Send, Clock, Eye, Copy, Mail, Upload, X as XIcon, Download,
} from "lucide-react";
const FORM_ICONS = { target: Target, search: Search, phone: Phone, globe: Globe };

export default function IntakeFormsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedForm = searchParams.get("form");
  const preselectedClient = searchParams.get("client");
  const preselectedProject = searchParams.get("project");
  const viewParam = searchParams.get("view"); // e.g., "pending" focuses on pending section

  const [activeForm, setActiveForm] = useState(preselectedForm || null);
  const [clients, setClients] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(preselectedClient || "");
  const [selectedProject, setSelectedProject] = useState(preselectedProject || "");
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewSubmission, setViewSubmission] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [resendingToken, setResendingToken] = useState(null);

  // Email-to-client state for instruction forms
  const [recipientEmail, setRecipientEmail] = useState("");
  const [contactName, setContactName] = useState("");
  const [sendingInstructions, setSendingInstructions] = useState(false);

  const allForms = getAllForms();

  useEffect(() => {
    Promise.all([
      getClients().catch(() => ({ data: [] })),
      getIntakeForms({}).catch(() => ({ data: [] })),
      getPendingIntakeRequests().catch(() => ({ data: [] })),
    ]).then(([c, s, p]) => {
      setClients(c.data);
      setSubmissions(s.data);
      setPendingRequests(p.data);
    }).finally(() => {
      setLoading(false);
      // If we landed here via the "View & Nudge" dashboard CTA (`?view=pending`),
      // scroll the user straight to the Pending section so they don't have to hunt.
      if (viewParam === "pending") {
        setTimeout(() => {
          const el = document.getElementById("pending-intake-section");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 250);
      }
    });
  }, [viewParam]);

  const reloadPending = async () => {
    try {
      const { data } = await getPendingIntakeRequests();
      setPendingRequests(data);
    } catch (e) { /* non-fatal */ }
  };

  const handleResend = async (token, email) => {
    setResendingToken(token);
    try {
      await resendIntakeShare(token);
      toast.success(`Reminder sent to ${email}`);
      await reloadPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend");
    } finally {
      setResendingToken(null);
    }
  };

  // Word-doc download: rep gets a white-labeled fillable .docx they can email directly.
  const [downloadingDocxId, setDownloadingDocxId] = useState(null);
  const handleDownloadDocx = async (form, opts = {}) => {
    const { event, silent = false } = opts;
    if (event) { event.stopPropagation(); event.preventDefault(); }
    setDownloadingDocxId(form.id);
    try {
      await downloadIntakeFormDocx({ schema: form, formName: form.name });
      if (!silent) toast.success("Word doc downloaded — attach it to your client email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't generate the Word doc");
      throw err;
    } finally {
      setDownloadingDocxId(null);
    }
  };

  const currentForm = activeForm ? getFormById(activeForm) : null;

  const updateField = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const toggleCheckbox = (fieldId, option) => {
    setFormData((prev) => {
      const current = prev[fieldId] || [];
      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [fieldId]: updated };
    });
  };

  const updateRankingCell = (fieldId, rowIndex, colKey, value) => {
    setFormData((prev) => {
      const rows = Array.isArray(prev[fieldId]) ? [...prev[fieldId]] : [];
      while (rows.length <= rowIndex) rows.push({});
      rows[rowIndex] = { ...rows[rowIndex], [colKey]: value };
      return { ...prev, [fieldId]: rows };
    });
  };

  const handleFileUpload = async (fieldId, file) => {
    if (!file) return;
    try {
      const { data } = await uploadFile(file);
      updateField(fieldId, { file_id: data.file_id, filename: file.name, size: file.size });
      toast.success(`${file.name} attached`);
    } catch (err) {
      toast.error("Upload failed — try again");
    }
  };

  const clearFile = (fieldId) => updateField(fieldId, null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) { toast.error("Please select a client"); return; }
    setSubmitting(true);
    try {
      await submitIntakeForm({
        form_type: activeForm,
        form_name: currentForm.name,
        client_id: selectedClient,
        project_id: selectedProject || undefined,
        data: formData,
      });
      toast.success("Form submitted — attached to the client's order, and admins notified.");
      setFormData({});
      setActiveForm(null);
      setSelectedProject("");
      // Refresh submissions
      const { data } = await getIntakeForms({});
      setSubmissions(data);
    } catch (err) {
      toast.error("Failed to submit form");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  // Form filling view
  if (activeForm && currentForm) {
    // Instruction-only forms (e.g., Facebook/Meta access guide)
    if (currentForm.type === "instruction") {
      const inst = currentForm.instruction || {};
      const copyLink = async () => {
        if (!inst.external_url) return;
        try {
          await navigator.clipboard.writeText(inst.external_url);
          toast.success("Link copied to clipboard");
        } catch { toast.error("Couldn't copy link"); }
      };
      const sendToClient = async (e) => {
        e.preventDefault();
        if (!recipientEmail) { toast.error("Enter a client email"); return; }
        setSendingInstructions(true);
        try {
          await sendIntakeInstructions({
            email: recipientEmail,
            title: currentForm.name,
            intro: inst.intro || "",
            steps: inst.steps || [],
            external_url: inst.external_url || "",
            contact_name: contactName,
          });
          toast.success(`Instructions sent to ${recipientEmail}`);
          setRecipientEmail("");
          setContactName("");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to send");
        } finally { setSendingInstructions(false); }
      };

      return (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="intake-instruction-view">
          <div>
            <button onClick={() => setActiveForm(null)} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2">
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to Forms
            </button>
            <h1 className="font-outfit text-3xl font-bold tracking-tight">{currentForm.name}</h1>
            <p className="text-muted-foreground mt-1">{currentForm.description}</p>
          </div>

          {/* Instructions */}
          <Card className="border">
            <CardContent className="pt-6 space-y-5">
              {inst.intro && <p className="text-sm leading-relaxed">{inst.intro}</p>}
              {inst.steps?.length > 0 && (
                <ol className="space-y-2 list-decimal list-inside text-sm">
                  {inst.steps.map((step, i) => <li key={i} className="leading-relaxed">{step}</li>)}
                </ol>
              )}
              {inst.external_url && (
                <div className="flex flex-wrap gap-2 pt-2">
                  <a
                    href={inst.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-md font-medium hover:bg-primary/90 transition"
                    data-testid="instruction-external-link"
                  >
                    <FileText className="h-4 w-4" />
                    Open Setup Guide
                  </a>
                  <Button type="button" variant="outline" onClick={copyLink} data-testid="copy-instruction-link">
                    <Copy className="mr-2 h-4 w-4" /> Copy Link
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Send to Client */}
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5" /> Send to Client
              </CardTitle>
              <CardDescription>Email these instructions directly to your client so they can grant access.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={sendToClient} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="contact_name" className="text-sm">Client First Name (optional)</Label>
                    <Input
                      id="contact_name"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      placeholder="e.g. Sarah"
                      className="mt-1"
                      data-testid="instruction-client-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="recipient_email" className="text-sm">Client Email</Label>
                    <Input
                      id="recipient_email"
                      type="email"
                      required
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="client@example.com"
                      className="mt-1"
                      data-testid="instruction-client-email"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={sendingInstructions} data-testid="send-instructions-btn">
                  {sendingInstructions ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : <><Send className="mr-2 h-4 w-4" />Send Instructions</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="intake-form-fill">
        <div>
          <button onClick={() => setActiveForm(null)} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to Forms
          </button>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1 className="font-outfit text-3xl font-bold tracking-tight">{currentForm.name}</h1>
              <p className="text-muted-foreground mt-1">{currentForm.description}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleDownloadDocx(currentForm)}
              disabled={downloadingDocxId === currentForm.id}
              data-testid={`download-docx-active-${currentForm.id}`}
              title="Download a blank, white-labeled Word version of this form"
            >
              {downloadingDocxId === currentForm.id
                ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                : <Download className="mr-1 h-4 w-4" />}
              Download as Word
            </Button>
          </div>
        </div>

        {currentForm.intro && (
          <div
            className="rounded-md border-l-4 border-primary bg-primary/5 px-4 py-3 text-sm leading-relaxed text-foreground/90"
            data-testid="intake-form-intro"
          >
            {currentForm.intro}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Select */}
          <Card className="border">
            <CardHeader><CardTitle className="text-lg">Client</CardTitle></CardHeader>
            <CardContent>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="form-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.company && `(${c.company})`}</SelectItem>)}</SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Form Sections */}
          {currentForm.sections.map((section, si) => (
            <Card key={si} className="border">
              <CardHeader>
                <CardTitle className="text-lg">{section.title}</CardTitle>
                {section.description && (
                  <CardDescription>{section.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {section.fields
                  .filter((field) => {
                    if (!field.condition) return true;
                    return formData[field.condition.field] === field.condition.value;
                  })
                  .map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label htmlFor={field.id}>
                      {field.label} {field.required && <span className="text-destructive">*</span>}
                    </Label>
                    {field.helper && (
                      <p className="text-xs text-muted-foreground -mt-1">{field.helper}</p>
                    )}

                    {field.type === "text" && (
                      <Input
                        id={field.id}
                        value={formData[field.id] || ""}
                        onChange={(e) => updateField(field.id, e.target.value)}
                        placeholder={field.placeholder || ""}
                        required={field.required}
                        data-testid={`field-${field.id}`}
                      />
                    )}

                    {field.type === "textarea" && (
                      <Textarea
                        id={field.id}
                        value={formData[field.id] || ""}
                        onChange={(e) => updateField(field.id, e.target.value)}
                        placeholder={field.placeholder || ""}
                        rows={3}
                        required={field.required}
                        data-testid={`field-${field.id}`}
                      />
                    )}

                    {field.type === "select" && (
                      <Select value={formData[field.id] || ""} onValueChange={(v) => updateField(field.id, v)}>
                        <SelectTrigger data-testid={`field-${field.id}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>{field.options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
                      </Select>
                    )}

                    {field.type === "checkboxes" && (
                      <div className="space-y-2">
                        {field.options.map((opt) => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={(formData[field.id] || []).includes(opt)}
                              onCheckedChange={() => toggleCheckbox(field.id, opt)}
                              data-testid={`field-${field.id}-${opt.toLowerCase().replace(/\s/g, "-")}`}
                            />
                            <span className="text-sm">{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}

                    {field.type === "ranking_table" && (
                      <div className="overflow-x-auto rounded-md border" data-testid={`field-${field.id}`}>
                        <table className="w-full text-sm">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="text-left font-medium px-3 py-2 w-16">Rank</th>
                              {field.columns.map((c) => (
                                <th key={c.key} className="text-left font-medium px-3 py-2">{c.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from({ length: field.rows || 5 }).map((_, ri) => {
                              const rowData = (formData[field.id] || [])[ri] || {};
                              return (
                                <tr key={ri} className="border-t">
                                  <td className="px-3 py-2 text-muted-foreground font-medium">{ri + 1}</td>
                                  {field.columns.map((c) => (
                                    <td key={c.key} className="px-2 py-1.5">
                                      {c.type === "textarea" ? (
                                        <Textarea
                                          value={rowData[c.key] || ""}
                                          onChange={(e) => updateRankingCell(field.id, ri, c.key, e.target.value)}
                                          placeholder={c.placeholder || ""}
                                          rows={1}
                                          className="resize-y min-h-[36px]"
                                          data-testid={`field-${field.id}-row${ri + 1}-${c.key}`}
                                        />
                                      ) : (
                                        <Input
                                          value={rowData[c.key] || ""}
                                          onChange={(e) => updateRankingCell(field.id, ri, c.key, e.target.value)}
                                          placeholder={c.placeholder || ""}
                                          data-testid={`field-${field.id}-row${ri + 1}-${c.key}`}
                                        />
                                      )}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {field.type === "file" && (
                      <div data-testid={`field-${field.id}`}>
                        {formData[field.id]?.file_id ? (
                          <div className="flex items-center justify-between gap-3 rounded-md border bg-secondary/30 px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-4 w-4 shrink-0 text-primary" />
                              <span className="text-sm truncate" title={formData[field.id].filename}>
                                {formData[field.id].filename}
                              </span>
                              {formData[field.id].size && (
                                <span className="text-xs text-muted-foreground shrink-0">
                                  ({(formData[field.id].size / 1024).toFixed(0)} KB)
                                </span>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => clearFile(field.id)}
                              data-testid={`field-${field.id}-clear`}
                            >
                              <XIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <label
                            htmlFor={`upload-${field.id}`}
                            className="flex items-center justify-center gap-2 rounded-md border border-dashed bg-secondary/20 px-4 py-6 cursor-pointer hover:bg-secondary/40 transition"
                          >
                            <Upload className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Click to upload</span>
                            <input
                              id={`upload-${field.id}`}
                              type="file"
                              className="hidden"
                              onChange={(e) => handleFileUpload(field.id, e.target.files?.[0])}
                              data-testid={`field-${field.id}-input`}
                            />
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => setActiveForm(null)}>Cancel</Button>
            <Button type="submit" disabled={submitting || !selectedClient} data-testid="submit-intake-form">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</> : <><Send className="mr-2 h-4 w-4" />Submit Form</>}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // Form listing view
  return (
    <div className="space-y-8" data-testid="intake-forms-page">
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight">Intake Forms</h1>
        <p className="text-muted-foreground mt-1">Fill out client intake forms or view past submissions</p>
      </div>

      {/* Available Forms */}
      <div>
        <h2 className="font-outfit text-xl font-semibold mb-4">Available Forms</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {allForms.map((form) => {
            const Icon = FORM_ICONS[form.icon] || FileText;
            const isInstruction = form.type === "instruction";
            return (
              <Card key={form.id} className="border card-hover cursor-pointer" onClick={() => { setActiveForm(form.id); setFormData({}); }}
                data-testid={`form-card-${form.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{form.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{form.description}</p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <Button variant="link" className="px-0 text-primary h-auto py-0" data-testid={`start-form-${form.id}`}>
                          {isInstruction ? "Read Instructions" : "Fill Out Form"} <ArrowLeft className="ml-1 h-3 w-3 rotate-180" />
                        </Button>
                        {!isInstruction && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
                            onClick={(e) => handleDownloadDocx(form, { event: e })}
                            disabled={downloadingDocxId === form.id}
                            data-testid={`download-docx-${form.id}`}
                            title="Download as Word document — email it to the client without sending a portal link"
                          >
                            {downloadingDocxId === form.id
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <Download className="h-3 w-3 mr-1" />}
                            Download as Word
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Pending Intake Requests — sent but not yet submitted */}
      {pendingRequests.length > 0 && (
        <div id="pending-intake-section" data-testid="pending-intake-section" className="scroll-mt-6">
          <h2 className="font-outfit text-xl font-semibold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600" />
            Awaiting Client Response ({pendingRequests.length})
          </h2>          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="pt-6">
              <div className="space-y-2">
                {pendingRequests.map((req) => {
                  const daysAgo = Math.floor((Date.now() - new Date(req.created_at).getTime()) / 86400000);
                  return (
                    <div
                      key={req.token}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white border border-amber-200"
                      data-testid={`pending-row-${req.token}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2 rounded-lg bg-amber-100 shrink-0">
                          <Clock className="h-4 w-4 text-amber-700" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">
                            {req.form_name || req.form_type}
                            {req.client_name && <span className="text-muted-foreground font-normal"> · {req.client_name}</span>}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            Sent to <b>{req.recipient_email}</b>
                            {" · "}
                            {daysAgo === 0 ? "today" : daysAgo === 1 ? "1 day ago" : `${daysAgo} days ago`}
                            {req.resend_count > 0 && ` · resent ${req.resend_count}×`}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            const schema = getFormById(req.form_type);
                            if (!schema) { toast.error("This form can't be downloaded as Word"); return; }
                            try {
                              await handleDownloadDocx(schema, { silent: true });
                            } catch {
                              return; // download failed, error already toasted
                            }
                            // Quietly cancel the pending share token so it stops showing up in
                            // "Awaiting Client Response" — the rep has pivoted to Word doc.
                            try {
                              await cancelIntakeShare(req.token, "rep_downloaded_docx");
                              toast.success("Word doc downloaded — pending link cleared");
                            } catch (err) {
                              // Non-fatal: download already happened
                              console.warn("Could not cancel share token:", err);
                              toast.success("Word doc downloaded");
                            }
                            await reloadPending();
                          }}
                          disabled={downloadingDocxId === req.form_type}
                          data-testid={`pending-download-docx-${req.token}`}
                          title="Send the client a Word doc instead — also clears this pending entry"
                        >
                          {downloadingDocxId === req.form_type
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : (<><Download className="mr-1 h-3.5 w-3.5" /> Word doc</>)}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleResend(req.token, req.recipient_email)}
                          disabled={resendingToken === req.token}
                          data-testid={`resend-${req.token}`}
                        >
                          {resendingToken === req.token ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (<><Send className="mr-1 h-3.5 w-3.5" /> Resend</>)}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Past Submissions */}
      {submissions.length > 0 && (
        <div>
          <h2 className="font-outfit text-xl font-semibold mb-4">Recent Submissions ({submissions.length})</h2>
          <Card className="border">
            <CardContent className="pt-6">
              <div className="space-y-3">
                {submissions.map((sub) => {
                  const clientName = clients.find((c) => c.id === sub.client_id)?.name || "Unknown Client";
                  return (
                    <div key={sub.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-secondary/30 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-100">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{sub.form_name}</p>
                          <p className="text-xs text-muted-foreground">{clientName} &middot; {formatDate(sub.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800">Submitted</Badge>
                        <Button variant="ghost" size="icon" onClick={() => setViewSubmission(sub)} data-testid={`view-submission-${sub.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Submission Dialog */}
      <Dialog open={!!viewSubmission} onOpenChange={() => setViewSubmission(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{viewSubmission?.form_name}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-3">
              {viewSubmission && Object.entries(viewSubmission.data || {}).map(([key, value]) => {
                const label = key.replace(/_/g, " ");
                // Ranking-table value: array of objects with column keys
                if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object" && value[0] !== null && !Array.isArray(value[0])) {
                  const rows = value.filter((r) => Object.values(r).some((v) => v));
                  if (rows.length === 0) return null;
                  return (
                    <div key={key} className="py-2 border-b last:border-0">
                      <div className="text-sm text-muted-foreground capitalize mb-2">{label}</div>
                      <div className="overflow-x-auto rounded-md border">
                        <table className="w-full text-xs">
                          <tbody>
                            {rows.map((row, i) => (
                              <tr key={i} className="border-t first:border-t-0">
                                <td className="px-2 py-1.5 text-muted-foreground w-8 align-top">{i + 1}</td>
                                {Object.entries(row).map(([k, v]) => (
                                  <td key={k} className="px-2 py-1.5 align-top">
                                    <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
                                    <div className="text-sm">{v || "—"}</div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                }
                // File attachment value: object with file_id + filename
                if (value && typeof value === "object" && value.file_id) {
                  const apiBase = `${API_BASE}/api`;
                  return (
                    <div key={key} className="flex justify-between py-2 border-b last:border-0">
                      <span className="text-sm text-muted-foreground capitalize">{label}</span>
                      <a
                        href={`${apiBase}/files/${value.file_id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {value.filename || "Attachment"}
                      </a>
                    </div>
                  );
                }
                return (
                  <div key={key} className="flex justify-between py-2 border-b last:border-0">
                    <span className="text-sm text-muted-foreground capitalize">{label}</span>
                    <span className="text-sm font-medium text-right max-w-[60%] whitespace-pre-wrap">
                      {Array.isArray(value) ? value.join(", ") : value || "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
